import React from "react";
import { Heart, Loader2, Play } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FlagIcon } from "@/components/voice/FlagIcon";
import { CompactLanguageFlags } from "@/components/voice/LanguageTooltip";
import { Voice } from "@/types/voice/voice";
import { getGenieImage } from "@/utils/image/genieAvatars";
import { getLanguageFromAccent } from "@/utils/localization/language";

interface VoiceCardProps {
  voice: Voice;
  viewMode: "list" | "compact" | "detailed";
  isPlaying: boolean;
  isFavorited: boolean;
  onPlay: (voice: Voice) => void;
  onToggleFavorite: (voice: Voice, e?: React.MouseEvent, fromAllVoicesTab?: boolean) => void;
  onTryText: (voice: Voice) => void;
}

export const VoiceCard: React.FC<VoiceCardProps> = ({ voice, viewMode, isPlaying, isFavorited, onPlay, onToggleFavorite, onTryText }) => {
  if (viewMode === "list") {
    return (
      <ListVoiceCard
        voice={voice}
        isPlaying={isPlaying}
        isFavorited={isFavorited}
        onPlay={onPlay}
        onToggleFavorite={onToggleFavorite}
        onTryText={onTryText}
      />
    );
  }

  if (viewMode === "compact") {
    return (
      <CompactVoiceCard
        voice={voice}
        isPlaying={isPlaying}
        isFavorited={isFavorited}
        onPlay={onPlay}
        onToggleFavorite={onToggleFavorite}
        onTryText={onTryText}
      />
    );
  }

  return (
    <DetailedVoiceCard
      voice={voice}
      isPlaying={isPlaying}
      isFavorited={isFavorited}
      onPlay={onPlay}
      onToggleFavorite={onToggleFavorite}
      onTryText={onTryText}
    />
  );
};

const ListVoiceCard: React.FC<VoiceCardProps> = ({ voice, isPlaying, isFavorited, onPlay, onToggleFavorite, onTryText }) => {
  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-white border border-gray-100 rounded-md hover:bg-gray-50/80 hover:border-gray-200 transition-all duration-150 group">
      {/* Avatar - clickable to play */}
      <button
        onClick={() => onPlay(voice)}
        disabled={isPlaying}
        className="relative w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 cursor-pointer group/avatar"
      >
        <img
          src={getGenieImage(voice.id, (voice.labels?.gender as "male" | "female" | "neutral") || "neutral")}
          alt={voice.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src = "/genie-mascots/role/support.png";
          }}
        />
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity rounded-full">
          {isPlaying ? (
            <Loader2 className="h-3.5 w-3.5 text-white animate-spin" />
          ) : (
            <Play className="h-3.5 w-3.5 text-white fill-white" />
          )}
        </div>
      </button>

      {/* Name & details */}
      <div className="min-w-0 flex-shrink-0" style={{ minWidth: '220px' }}>
        <h3 className="font-medium text-sm text-gray-900 truncate">{voice.name}</h3>
        <p className="text-[11px] text-gray-400 whitespace-nowrap">
          {[voice.labels?.gender, voice.labels?.age, voice.labels?.accent, voice.category].filter(Boolean).join(" · ")}
        </p>
      </div>

      {/* Language Flags */}
      <div className="hidden md:block flex-shrink-0">
        <CompactLanguageFlags
          languages={
            voice.verified_languages?.length > 0
              ? voice.verified_languages.filter((l) => l && l.language)
              : [
                  {
                    language: voice.labels?.accent ? getLanguageFromAccent(voice.labels.accent) : voice.labels?.language || "en",
                    accent: voice.labels?.accent,
                  },
                ]
          }
          maxDisplay={4}
          size="sm"
        />
      </div>

      {/* Description */}
      <p className="hidden lg:block flex-1 text-xs text-gray-400 truncate min-w-0">
        {voice.description || "Professional voice for customer interactions"}
      </p>

      {/* Actions */}
      <div className="flex items-center gap-0.5 flex-shrink-0 ml-auto">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onTryText(voice)}
          className="h-7 px-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
        >
          Try Text
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => onToggleFavorite(voice, e)}
          className="h-7 w-7 p-0 hover:bg-gray-100/50"
        >
          {isFavorited ? (
            <>
              <svg width="0" height="0" className="absolute">
                <defs>
                  <linearGradient id={`gradient-list-${voice.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#667eea" />
                    <stop offset="100%" stopColor="#764ba2" />
                  </linearGradient>
                </defs>
              </svg>
              <Heart
                className="h-4 w-4"
                style={{
                  fill: `url(#gradient-list-${voice.id})`,
                  stroke: `url(#gradient-list-${voice.id})`,
                }}
              />
            </>
          ) : (
            <Heart className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          )}
        </Button>
      </div>
    </div>
  );
};

const CompactVoiceCard: React.FC<VoiceCardProps> = ({ voice, isPlaying, isFavorited, onPlay, onToggleFavorite, onTryText }) => {
  return (
    <Card className="hover:shadow-md transition-all duration-200 overflow-hidden group relative">
      <CardContent className="p-3">
        {/* Favorite button - always visible in top right */}
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => onToggleFavorite(voice, e)}
          className="absolute top-2 right-2 h-8 w-8 p-0 z-10 hover:bg-gray-100/50"
        >
          {isFavorited ? (
            <>
              <svg width="0" height="0" className="absolute">
                <defs>
                  <linearGradient id={`gradient-${voice.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#667eea" />
                    <stop offset="100%" stopColor="#764ba2" />
                  </linearGradient>
                </defs>
              </svg>
              <Heart
                className="h-5 w-5"
                style={{
                  fill: `url(#gradient-${voice.id})`,
                  stroke: `url(#gradient-${voice.id})`,
                }}
              />
            </>
          ) : (
            <Heart className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          )}
        </Button>

        <div className="flex items-start gap-3">
          {/* Genie Avatar */}
          <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10">
            <img
              src={getGenieImage(voice.id, (voice.labels?.gender as "male" | "female" | "neutral") || "neutral")}
              alt={voice.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src = "/genie-mascots/role/support.png";
              }}
            />
          </div>

          {/* Voice Info */}
          <div className="flex-1 min-w-0 pr-6">
            {" "}
            {/* Added padding-right for favorite button space */}
            <h3 className="font-semibold text-sm text-gray-900 truncate">{voice.name}</h3>
            {voice.category && <p className="text-xs text-gray-500 truncate">{voice.category}</p>}
            {/* Compact Labels */}
            <div className="flex items-center gap-1 mt-1">
              {voice.labels?.gender && <span className="text-xs text-gray-600">{voice.labels.gender}</span>}
              {voice.labels?.age && (
                <>
                  <span className="text-xs text-gray-400">•</span>
                  <span className="text-xs text-gray-600">{voice.labels.age}</span>
                </>
              )}
              {voice.labels?.accent && (
                <>
                  <span className="text-xs text-gray-400">•</span>
                  <span className="text-xs text-gray-600">{voice.labels.accent}</span>
                </>
              )}
            </div>
            {/* Language Flags - Show properly filtered languages */}
            <div className="mt-1">
              <CompactLanguageFlags
                languages={
                  voice.verified_languages?.length > 0
                    ? voice.verified_languages.filter((l) => l && l.language) // Filter out empty entries
                    : [
                        {
                          language: voice.labels?.accent ? getLanguageFromAccent(voice.labels.accent) : voice.labels?.language || "en",
                          accent: voice.labels?.accent,
                        },
                      ]
                }
                maxDisplay={5}
                size="sm"
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-600 line-clamp-2 mt-2 min-h-[32px]">
          {voice.description || "Professional voice for customer interactions"}
        </p>

        {/* Actions - Show on hover (except favorite which is always visible) */}
        <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onPlay(voice);
            }}
            disabled={isPlaying}
            className="flex-1 h-8 text-xs"
          >
            {isPlaying ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <>
                <Play className="h-3 w-3 mr-1" />
                Play
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onTryText(voice);
            }}
            className="h-8 px-3 text-xs"
          >
            Try Text
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const DetailedVoiceCard: React.FC<VoiceCardProps> = ({ voice, isPlaying, isFavorited, onPlay, onToggleFavorite, onTryText }) => {
  return (
    <Card className="hover:shadow-lg transition-shadow duration-200 overflow-hidden group relative">
      <CardContent className="p-6">
        {/* Voice Header with Avatar */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3 flex-1">
            {/* Genie Avatar */}
            <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10">
              <img
                src={getGenieImage(voice.id, (voice.labels?.gender as "male" | "female" | "neutral") || "neutral")}
                alt={voice.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = "/genie-mascots/role/support.png";
                }}
              />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{voice.name}</h3>
              {voice.category && <p className="text-sm text-gray-500">{voice.category}</p>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={(e) => onToggleFavorite(voice, e)} className="h-9 w-9 p-0">
              {isFavorited ? (
                <>
                  <svg width="0" height="0" className="absolute">
                    <defs>
                      <linearGradient id={`gradient-detailed-${voice.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#667eea" />
                        <stop offset="100%" stopColor="#764ba2" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <Heart
                    className="h-5 w-5"
                    style={{
                      fill: `url(#gradient-detailed-${voice.id})`,
                      stroke: `url(#gradient-detailed-${voice.id})`,
                    }}
                  />
                </>
              ) : (
                <Heart className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </Button>
            <Button
              size="sm"
              variant={isPlaying ? "default" : "outline"}
              onClick={(e) => {
                e.stopPropagation();
                onPlay(voice);
              }}
              disabled={isPlaying}
            >
              {isPlaying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onTryText(voice);
              }}
            >
              Try Text
            </Button>
          </div>
        </div>

        {/* Description */}
        {voice.description && <p className="text-sm text-gray-600 mb-4 line-clamp-2">{voice.description}</p>}

        {/* Labels */}
        {voice.labels && (
          <div className="flex flex-wrap gap-2 mb-3">
            {voice.labels.accent && (
              <Badge variant="secondary" className="text-xs">
                {voice.labels.accent}
              </Badge>
            )}
            {voice.labels.age && (
              <Badge variant="outline" className="text-xs">
                {voice.labels.age}
              </Badge>
            )}
            {voice.labels.gender && (
              <Badge variant="outline" className="text-xs">
                {voice.labels.gender}
              </Badge>
            )}
            {voice.labels.use_case && (
              <Badge className="text-xs bg-genie-primary/10 text-genie-primary hover:bg-genie-primary/20">{voice.labels.use_case}</Badge>
            )}
          </div>
        )}

        {/* Verified Languages with Flags - Always show something */}
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-gray-700">Languages:</span>
            <CompactLanguageFlags
              languages={
                voice.verified_languages?.length > 0
                  ? voice.verified_languages.filter((l) => l && l.language) // Filter out empty entries
                  : [
                      {
                        language: voice.labels?.accent ? getLanguageFromAccent(voice.labels.accent) : voice.labels?.language || "en",
                        accent: voice.labels?.accent,
                      },
                    ]
              }
              maxDisplay={8}
              size="base"
            />
          </div>
          {voice.verified_languages && voice.verified_languages.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {voice.verified_languages.slice(0, 4).map((l, idx) => (
                <span
                  key={`${voice.id}-lang-${idx}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs border border-blue-100"
                  title={[l.language, l.accent, l.locale].filter(Boolean).join(" • ")}
                  aria-label={`Language ${l.language}${l.accent ? `, accent ${l.accent}` : ""}`}
                >
                  <FlagIcon language={l.language} accent={l.accent} size="sm" />
                  <span className="ml-0.5">
                    {l.language}
                    {l.accent ? ` — ${l.accent}` : ""}
                  </span>
                </span>
              ))}
              {voice.verified_languages.length > 4 && (
                <span className="px-2 py-0.5 rounded-full bg-gray-50 text-gray-700 text-xs border border-gray-200">
                  +{voice.verified_languages.length - 4} more languages
                </span>
              )}
            </div>
          ) : (
            // Fallback when no verified_languages
            <div className="flex flex-wrap gap-1.5">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs border border-blue-100">
                <FlagIcon
                  language={voice.labels?.accent ? getLanguageFromAccent(voice.labels.accent) : voice.labels?.language || "en"}
                  accent={voice.labels?.accent}
                  size="sm"
                />
                <span className="ml-0.5">
                  English
                  {voice.labels?.accent && ` — ${voice.labels.accent}`}
                </span>
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default VoiceCard;
